import {
  AnalogueChartKey,
  AnalogueManualSettings,
  ScenarioLayerAnalogueHit,
} from '@/api/scenarioLayers';
import {
  buildDefaultProfilesFromCoefs,
  DEFAULT_PROFILE_HORIZON_YEARS,
} from '@/utils/layerProductionCharts';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Slider,
  Switch,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const PALETTE = [
  '#5c6bc0',
  '#26a69a',
  '#7e57c2',
  '#42a5f5',
  '#66bb6a',
  '#ab47bc',
  '#29b6f6',
  '#8d6e63',
  '#ec407a',
  '#78909c',
];
const MANUAL_COLOR = '#e65100';

interface AnalogueManualCurveDialogProps {
  open: boolean;
  chartKey: AnalogueChartKey | null;
  analogues: ScenarioLayerAnalogueHit[];
  manual?: AnalogueManualSettings;
  saving?: boolean;
  onClose: () => void;
  onSave: (chartKey: AnalogueChartKey, manual: AnalogueManualSettings[AnalogueChartKey]) => void;
}

function median(values: number[]): number | null {
  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length) return null;
  finite.sort((a, b) => a - b);
  const mid = Math.floor(finite.length / 2);
  return finite.length % 2 ? finite[mid] : (finite[mid - 1] + finite[mid]) / 2;
}

function defaultOilCoefs(analogues: ScenarioLayerAnalogueHit[]) {
  return {
    q0: median(analogues.map((a) => a.q0 ?? NaN)) ?? 1,
    a: median(analogues.map((a) => a.a ?? NaN)) ?? 0.1,
  };
}

function defaultLiquidCoefs(analogues: ScenarioLayerAnalogueHit[]) {
  const oil = defaultOilCoefs(analogues);
  return {
    ...oil,
    wc0: median(analogues.map((a) => a.wc0 ?? NaN)) ?? 0.1,
    k: median(analogues.map((a) => a.k ?? NaN)) ?? 0.5,
  };
}

function defaultWcCoefs(analogues: ScenarioLayerAnalogueHit[]) {
  return {
    wc0: median(analogues.map((a) => a.wc0 ?? NaN)) ?? 0.1,
    k: median(analogues.map((a) => a.k ?? NaN)) ?? 0.5,
  };
}

function defaultRecovery(analogues: ScenarioLayerAnalogueHit[]) {
  const vals = analogues.map((a) => a.recovery_factor).filter((v): v is number => v != null);
  return median(vals) ?? 0.3;
}

function expandSliderRange(
  values: number[],
  fallback: { min: number; max: number },
  minFloor = 0,
): { min: number; max: number } {
  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length) return fallback;
  const lo = Math.min(...finite);
  const hi = Math.max(...finite);
  const center = (lo + hi) / 2;
  const halfSpan = Math.max((hi - lo) / 2, Math.abs(center) * 0.25, 0.05);
  const min = Math.max(minFloor, center - halfSpan * 2);
  const max = Math.max(center + halfSpan * 2, min + 0.01);
  return { min, max };
}

function stepForRange(min: number, max: number): number {
  const span = Math.abs(max - min);
  if (span >= 10) return 0.01;
  if (span >= 1) return 0.001;
  return 0.0001;
}

function buildSliderRanges(
  analogues: ScenarioLayerAnalogueHit[],
  manual?: AnalogueManualSettings,
): Record<string, { min: number; max: number; step: number }> {
  const q0s = analogues.map((a) => a.q0 ?? NaN);
  const as = analogues.map((a) => a.a ?? NaN);
  const wc0s = analogues.map((a) => a.wc0 ?? NaN);
  const ks = analogues.map((a) => a.k ?? NaN);
  const rfs = analogues.map((a) => a.recovery_factor ?? NaN);

  if (manual?.oil?.q0 != null) q0s.push(manual.oil.q0);
  if (manual?.oil?.a != null) as.push(manual.oil.a);
  if (manual?.liquid?.q0 != null) q0s.push(manual.liquid.q0);
  if (manual?.liquid?.a != null) as.push(manual.liquid.a);
  if (manual?.liquid?.wc0 != null) wc0s.push(manual.liquid.wc0);
  if (manual?.liquid?.k != null) ks.push(manual.liquid.k);
  if (manual?.water_cut_pct?.wc0 != null) wc0s.push(manual.water_cut_pct.wc0);
  if (manual?.water_cut_pct?.k != null) ks.push(manual.water_cut_pct.k);
  if (manual?.recovery_factor?.value != null) rfs.push(manual.recovery_factor.value);

  const q0Range = expandSliderRange(q0s, { min: 0.01, max: 10 }, 0.01);
  const aRange = expandSliderRange(as, { min: 0, max: 1 }, 0);
  const wc0Range = { min: 0, max: 0.95 };
  const kRange = { min: 0.1, max: 1 };
  const recoveryRange = expandSliderRange(rfs, { min: 0, max: 1 }, 0);

  return {
    q0: { ...q0Range, step: stepForRange(q0Range.min, q0Range.max) },
    a: { ...aRange, step: stepForRange(aRange.min, aRange.max) },
    wc0: { ...wc0Range, step: 0.001 },
    k: { ...kRange, step: 0.01 },
    recovery: { ...recoveryRange, step: 0.001 },
  };
}

function chartTitle(chartKey: AnalogueChartKey): string {
  if (chartKey === 'oil') return 'Oil decline';
  if (chartKey === 'liquid') return 'Liquid rate';
  if (chartKey === 'water_cut_pct') return 'Water cut';
  return 'Recovery factor';
}

function chartYLabel(chartKey: AnalogueChartKey): string {
  if (chartKey === 'water_cut_pct') return '%';
  if (chartKey === 'recovery_factor') return 'Recovery factor';
  return 'thous. t';
}

function buildManualSeries(
  chartKey: AnalogueChartKey,
  draft: AnalogueManualSettings[AnalogueChartKey],
  analogues: ScenarioLayerAnalogueHit[],
): number[] {
  if (chartKey === 'recovery_factor') {
    const rf = draft as AnalogueManualSettings['recovery_factor'];
    return rf.value != null ? [rf.value] : [];
  }
  if (chartKey === 'oil') {
    const d = draft as AnalogueManualSettings['oil'];
    if (d.q0 == null || d.a == null) return [];
    return buildDefaultProfilesFromCoefs({ q0: d.q0, a: d.a, wc0: null, k: null }).map((p) => p.oil ?? 0);
  }
  if (chartKey === 'liquid') {
    const d = draft as AnalogueManualSettings['liquid'];
    if (d.q0 == null || d.a == null || d.wc0 == null || d.k == null) return [];
    return buildDefaultProfilesFromCoefs({ q0: d.q0, a: d.a, wc0: d.wc0, k: d.k }).map((p) => p.liquid ?? 0);
  }
  const d = draft as AnalogueManualSettings['water_cut_pct'];
  if (d.wc0 == null || d.k == null) return [];
  const { q0, a } = defaultOilCoefs(analogues);
  return buildDefaultProfilesFromCoefs({ q0, a, wc0: d.wc0, k: d.k }).map((p) => p.waterCut ?? 0);
}

interface SliderFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}

const SliderField: React.FC<SliderFieldProps> = ({ label, value, min, max, step, onChange }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="body2" sx={{ mb: 0.5 }}>
      {label}: {value.toFixed(4)}
    </Typography>
    <Slider
      value={value}
      min={min}
      max={max}
      step={step}
      valueLabelDisplay="auto"
      onChange={(_, v) => onChange(v as number)}
    />
  </Box>
);

export const AnalogueManualCurveDialog: React.FC<AnalogueManualCurveDialogProps> = ({
  open,
  chartKey,
  analogues,
  manual,
  saving = false,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState<AnalogueManualSettings[AnalogueChartKey] | null>(null);

  const sliderRanges = useMemo(() => {
    if (!open || !chartKey) return null;
    return buildSliderRanges(analogues, manual);
  }, [open, chartKey, analogues, manual]);

  useEffect(() => {
    if (!open || !chartKey) {
      setDraft(null);
      return;
    }
    const existing = manual?.[chartKey];
    if (chartKey === 'oil') {
      const defaults = defaultOilCoefs(analogues);
      setDraft({
        enabled: existing?.enabled ?? false,
        q0: (existing as AnalogueManualSettings['oil'])?.q0 ?? defaults.q0,
        a: (existing as AnalogueManualSettings['oil'])?.a ?? defaults.a,
      });
      return;
    }
    if (chartKey === 'liquid') {
      const defaults = defaultLiquidCoefs(analogues);
      setDraft({
        enabled: existing?.enabled ?? false,
        q0: (existing as AnalogueManualSettings['liquid'])?.q0 ?? defaults.q0,
        a: (existing as AnalogueManualSettings['liquid'])?.a ?? defaults.a,
        wc0: (existing as AnalogueManualSettings['liquid'])?.wc0 ?? defaults.wc0,
        k: (existing as AnalogueManualSettings['liquid'])?.k ?? defaults.k,
      });
      return;
    }
    if (chartKey === 'water_cut_pct') {
      const defaults = defaultWcCoefs(analogues);
      setDraft({
        enabled: existing?.enabled ?? false,
        wc0: (existing as AnalogueManualSettings['water_cut_pct'])?.wc0 ?? defaults.wc0,
        k: (existing as AnalogueManualSettings['water_cut_pct'])?.k ?? defaults.k,
      });
      return;
    }
    setDraft({
      enabled: existing?.enabled ?? false,
      value: (existing as AnalogueManualSettings['recovery_factor'])?.value ?? defaultRecovery(analogues),
    });
  }, [open, chartKey, analogues, manual]);

  const previewData = useMemo(() => {
    if (!chartKey || !draft) return [];
    if (chartKey === 'recovery_factor') return [];
    const n = Math.max(
      DEFAULT_PROFILE_HORIZON_YEARS,
      ...analogues.map((a) => {
        if (chartKey === 'oil') return a.curves?.oil?.length ?? 0;
        if (chartKey === 'liquid') return a.curves?.liquid?.length ?? 0;
        return a.curves?.water_cut_pct?.length ?? 0;
      }),
      1,
    );
    const manualSeries = buildManualSeries(chartKey, draft, analogues);
    return Array.from({ length: n }, (_, year) => {
      const point: Record<string, number | null> = { year };
      analogues.forEach((col, idx) => {
        const key = chartKey === 'water_cut_pct' ? 'water_cut_pct' : chartKey;
        const series = col.curves?.[key] || [];
        point[`a${idx}`] = year < series.length ? series[year] : null;
      });
      point.manual = year < manualSeries.length ? manualSeries[year] : null;
      return point;
    });
  }, [chartKey, draft, analogues]);

  if (!chartKey || !draft || !sliderRanges) {
    return null;
  }

  const handleSave = () => {
    onSave(chartKey, draft);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Set {chartTitle(chartKey).toLowerCase()} manually</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Adjust equation parameters against analogue curves. The manual curve is saved and used in
          production calculations when enabled.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={draft.enabled}
              onChange={(_, checked) => setDraft({ ...draft, enabled: checked })}
            />
          }
          label="Use manual curve in calculations"
          sx={{ mb: 2 }}
        />

        {chartKey === 'oil' && (
          <>
            <SliderField
              label="q₀ (initial oil rate, thous. t)"
              value={(draft as AnalogueManualSettings['oil']).q0 ?? 0}
              min={sliderRanges.q0.min}
              max={sliderRanges.q0.max}
              step={sliderRanges.q0.step}
              onChange={(q0) => setDraft({ ...draft, q0 })}
            />
            <SliderField
              label="a (decline rate, 1/year)"
              value={(draft as AnalogueManualSettings['oil']).a ?? 0}
              min={sliderRanges.a.min}
              max={sliderRanges.a.max}
              step={sliderRanges.a.step}
              onChange={(a) => setDraft({ ...draft, a })}
            />
          </>
        )}

        {chartKey === 'liquid' && (
          <>
            <SliderField
              label="q₀"
              value={(draft as AnalogueManualSettings['liquid']).q0 ?? 0}
              min={sliderRanges.q0.min}
              max={sliderRanges.q0.max}
              step={sliderRanges.q0.step}
              onChange={(q0) => setDraft({ ...draft, q0 })}
            />
            <SliderField
              label="a"
              value={(draft as AnalogueManualSettings['liquid']).a ?? 0}
              min={sliderRanges.a.min}
              max={sliderRanges.a.max}
              step={sliderRanges.a.step}
              onChange={(a) => setDraft({ ...draft, a })}
            />
            <SliderField
              label="wc₀"
              value={(draft as AnalogueManualSettings['liquid']).wc0 ?? 0}
              min={sliderRanges.wc0.min}
              max={sliderRanges.wc0.max}
              step={sliderRanges.wc0.step}
              onChange={(wc0) => setDraft({ ...draft, wc0 })}
            />
            <SliderField
              label="k"
              value={(draft as AnalogueManualSettings['liquid']).k ?? 0}
              min={sliderRanges.k.min}
              max={sliderRanges.k.max}
              step={sliderRanges.k.step}
              onChange={(k) => setDraft({ ...draft, k })}
            />
          </>
        )}

        {chartKey === 'water_cut_pct' && (
          <>
            <SliderField
              label="wc₀"
              value={(draft as AnalogueManualSettings['water_cut_pct']).wc0 ?? 0}
              min={sliderRanges.wc0.min}
              max={sliderRanges.wc0.max}
              step={sliderRanges.wc0.step}
              onChange={(wc0) => setDraft({ ...draft, wc0 })}
            />
            <SliderField
              label="k"
              value={(draft as AnalogueManualSettings['water_cut_pct']).k ?? 0}
              min={sliderRanges.k.min}
              max={sliderRanges.k.max}
              step={sliderRanges.k.step}
              onChange={(k) => setDraft({ ...draft, k })}
            />
          </>
        )}

        {chartKey === 'recovery_factor' && (
          <SliderField
            label="Recovery factor"
            value={(draft as AnalogueManualSettings['recovery_factor']).value ?? 0}
            min={sliderRanges.recovery.min}
            max={sliderRanges.recovery.max}
            step={sliderRanges.recovery.step}
            onChange={(value) => setDraft({ ...draft, value })}
          />
        )}

        {chartKey !== 'recovery_factor' && (
          <Box sx={{ height: 280, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={previewData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={48} label={{ value: chartYLabel(chartKey), angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {analogues.map((col, idx) => (
                  <Line
                    key={col.layer_id}
                    type="monotone"
                    dataKey={`a${idx}`}
                    name={`${col.field_name} / ${col.layer_name}`}
                    stroke={PALETTE[idx % PALETTE.length]}
                    dot={false}
                    strokeWidth={1.2}
                    isAnimationActive={false}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="manual"
                  name="Manual"
                  stroke={MANUAL_COLOR}
                  dot={false}
                  strokeWidth={2.5}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
